<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    v-if="techSpecs.length"
    data-selector="product-tech-specs"
    :class="[bem(), 'ignore-theme']"
  >
    <div :class="bem('heading-wrapper')">
      <h2 :class="bem('heading')">
        <span :class="bem('heading', { highlight: true })">
          {{ productName }}
        </span>
        {{ $tr('Features') }}
      </h2>
      <div
        v-show="techSpecs.length > 2"
        :class="
          bem('controls', {
            hidden: !leftArrowVisible && !rightArrowVisible,
          })
        "
      >
        <CarouselArrow
          arrow="left"
          :is-disabled="!leftArrowVisible"
          :class="bem('arrow', {
            hidden: !leftArrowVisible
          })"
          background-colour="dark-grey"
        />
        <CarouselArrow
          arrow="right"
          :is-disabled="!rightArrowVisible"
          background-colour="dark-grey"
        />
      </div>
    </div>
    <Swiper
      :space-between="'8px'"
      :slides-per-view="1.5"
      :breakpoints="{
        500: {
          slidesPerView: 1.8,
        },
        600: {
          slidesPerView: 2.3,
        },
        930: {
          slidesPerView: 2.5,
        },
        1100: {
          slidesPerView: 3.2,
        },
        1400: {
          slidesPerView: 3.5,
        }
      }"
      :modules="[ Navigation ]"
      :navigation="{
        nextEl: `[data-selector=product-tech-specs] [data-carousel-arrow=right]`,
        prevEl: `[data-selector=product-tech-specs] [data-carousel-arrow=left]`,
      }"
      :class="bem('carousel')"
      @reach-end="handleReachEnd"
      @slide-change="handleSlideChange"
    >
      <SwiperSlide
        v-for="spec in visibleTechSpecs"
        :key="spec.id"
        :class="bem('slide')"
      >
        <div :class="bem('feature')">
          <div :class="bem('feature-container')">
            <div :class="bem('feature-icon-container')">
              <VisionIcon
                :id="'product-tech-specs-' + formatIconName(spec.icon)"
                :icon="formatIconName(spec.icon)"
                :gradient="
                  pageTheme !== 'default' ? pageTheme : 'default-light'
                "
                size="x-large"
                :class="
                  bem('feature-icon', { icon: formatIconName(spec.icon) })
                "
              />
            </div>
            <div>
              <p :class="bem('feature-title')">
                {{ $tr(spec.label) }}
              </p>
              <p
                :class="bem('feature-value')"
                v-html="$tr(spec.value)"
              />
            </div>
          </div>
        </div>
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<script setup>
import CustomisedProduct from '~/models/CustomisedProduct'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'

const bem = useBEM('ProductTechSpecs')

const props = defineProps({
  customisedProduct: { type: CustomisedProduct, required: true },
})

const { pageTheme } = storeToRefs(usePageStore())

const leftArrowVisible = ref(false)
const rightArrowVisible = ref(true)

const techSpecs = computed(() => {
  return (
    props.customisedProduct?.product?.pdpConfigWithDefaults?.techSpecs || []
  )
})

const visibleTechSpecs = computed(() => {
  return techSpecs.value.filter((spec) =>
    (lensTech.value || '').match(spec.lensTechFilter || '.*')
  )
})

const lensTech = computed(() => {
  return props.customisedProduct.lensTech
})

const productName = computed(() => {
  if (
    props.customisedProduct.isLimitedEdition &&
    !props.customisedProduct.product.useBaseProductName
  )
    return props.customisedProduct.product.name

  if (props.customisedProduct.product.baseProduct?.name)
    return props.customisedProduct.product.baseProduct.name

  return props.customisedProduct.product.name
})

function formatIconName(icon) {
  return icon.replace(/^(sgi-)/, '')
}

function handleSlideChange(swiper) {
  leftArrowVisible.value = !swiper.isBeginning
  rightArrowVisible.value = !swiper.isEnd
}

function handleReachEnd() {
  rightArrowVisible.value = false
}
</script>

<style lang="postcss">
.ProductTechSpecs {
  @apply relative
    py-10 sm:py-14;

  &.ignore-theme {
    @apply bg-black text-white;
  }
}

.ProductTechSpecs__slide {
  @apply w-[66%] mr-2 rounded-lg overflow-hidden;

  @media (min-width: 500px) {
    @apply w-[55%];
  }

  @media (min-width: 600px) {
    @apply w-[43%];
  }

  @media (min-width: 930px) {
    @apply w-[39.5%];
  }

  @media (min-width: 1100px) {
    @apply w-[30.3%];
  }

  @media (min-width: 1400px) {
    @apply w-[28%];
  }
}

.ProductTechSpecs__heading-wrapper {
  @apply global-wrapper
    flex justify-between items-start gap-2;
}

.ProductTechSpecs__carousel {
  @apply carousel-padding;
}

.ProductTechSpecs__heading {
  @apply heading-2
    mb-8;

  &--highlight {
    @apply md:inline mb-0
      text-grey-600;
  }
}

.ProductTechSpecs__feature {
  @apply flex flex-nowrap flex-col justify-end
    aspect-[16/10]
    bg-off-black;
}

.ProductTechSpecs__feature-container {
  @apply px-4 py-5;

  background: linear-gradient(0deg, rgba(0, 0, 0, 0.48) 0%, rgba(0, 0, 0, 0.00) 100%);
}

.ProductTechSpecs__feature-icon-container {
  @apply w-10 mb-5;
}

.ProductTechSpecs__feature-title {
  @apply text-sm md:text-lg text-white leading-tight font-semibold
    mb-1;
}

.ProductTechSpecs__feature-value {
  @apply text-sm md:text-base opacity-50 text-white leading-normal;
}

.ProductTechSpecs__controls {
  @apply flex gap-3;
}
</style>
